/* global console */

import {
  CoObjectItem,
  CoObjectParameters,
  DimensionDetails,
  DocumentPartSource,
  TgkAmountObjectSource,
  TgkDimensionAttributeSource,
  TgkDropDownSource,
  TgkTableObjectSource,
  TgkTextSource,
} from "../../../components/interfaces/CoObject";
import {
  CoObject,
  CoObjectDocumentPartInclude,
  CoObjectDropdown,
  CoObjectTypeEnum,
  DocumentPart,
  SourceEnum,
  TgkAmountElaborationRequest,
  TgkDimensionAttributeElaborationRequest,
  TgkTableElaborationRequest,
  TgkTextElaborationRequest,
} from "../../../api/models";
import { SourceTypeEnum } from "../../../api/models/source-type-enum";
import { store } from "../../store/store";
import { TgkTableParameter } from "../../../api/models/tgk-table-parameter";
import {
  AdvancedDocumentMetadata,
  getDocumentAdvancedCustomXml,
} from "../../../utils/helpers/DocumentAdvancedCustomXmlHelper";
import { FormatMetadata } from "../../../api/models/format-metadata";
import { canInherit } from "../../../utils/helpers/ObjectFormattingHelper";
import NotificationManager from "../../../common/notification/NotificationManager";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { FormatTypeEnum } from "../../../api/models/format-type-enum";
import { DocumentPartRelativePosition } from "../../../office/WordDocumentPart";
import { DocumentParameters } from "../../../components/document/ParametersDocument";
import { normalizeDocumentName } from "../../../utils/helpers/ObjectCheckerHelper";
import { normalizePath } from "../document/document-slice";
import * as uuid from "uuid";
import { ElaborationItem, UpdateOptions } from "../../../api/object/RefreshObject";
import { TableUpdateStrategy } from "../../../components/coobjects/co-panel/ObjectPanelComponent";
import { ComponentInstanceElaborationRequest } from "../../../api/models/component-instance-elaboration-request";

export function mapComponentInstanceElaborationRequest(
  coObject: CoObjectItem,
  userId: string
): ComponentInstanceElaborationRequest {
  const request: ComponentInstanceElaborationRequest = {
    correlationId: uuid.v4(),
    clientId: store.getState().configurationState.clientId,
    userId: userId,
    objectName: coObject.info.objectName,
    objectInfo: coObject.info.info,
    objectType: CoObjectTypeEnum[coObject.info.objectType],
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId,
    componentLogicId: coObject.info.componentReference.componentLogicId,
    documentName: "",
    rootLanguage: "",
  };

  if (coObject.id) {
    request.id = coObject.id;
  }

  return request;
}

export function mapTgkTableConfigToTgkTable(
  coObject: CoObjectItem,
  userId: string,
  tableUpdateType: TableUpdateStrategy["typeUpdate"]
): TgkTableElaborationRequest {
  const source = coObject.source as TgkTableObjectSource;
  return {
    id: coObject.id,
    objectType: CoObjectTypeEnum[coObject.info.objectType],
    source: SourceEnum[source.objectSource],
    name: coObject.info.objectName,
    desc: coObject.info.info,
    isComponent: coObject.info.isComponent,
    documentId: coObject.documentId,
    isLocked: coObject.isLocked,
    form: {
      dbId: source.objectSource !== SourceTypeEnum.EXCEL ? source.dbApplication.id : "",
      formId: source.form.code,
      formDesc: source.form.desc,
      path: source.form.oidPath ? source.form.oidPath.toString() : "",
    },
    sheetCode: source.sheet.code,
    sheet:
      source.objectSource !== SourceTypeEnum.EXCEL || coObject.info?.objectType === "CHART"
        ? source.sheet.desc
        : getSheetForExcelTableObject(source),
    area: source.area?.name,
    chart: source.chart?.name,
    contentControlId: `${coObject.contentControlId}`,
    clientId: store.getState().configurationState.clientId,
    correlationId: uuid.v4(),
    userId: userId,
    objectId: coObject.info.objectName,
    formatMetadata: mapAdvancedTableToDocPartMetadata(coObject.advanced, coObject.advanced.locale),
    ...(source.elabResultType === "IMAGE_STRINGBASE64" && !source.inheritFormatImage
      ? {
          formatImage: {
            imageExtension: source.imageExtension,
            ...(source.imageResolution ? { imageResolution: source.imageResolution } : {}),
          },
        }
      : {}),
    parameters: coObject.parameters?.map((p) => mapParameterTableToTgkParameter(p)),
    inheritFormat: coObject.advanced.inheritFormat,
    inheritFormatImage: source.elabResultType === "IMAGE_STRINGBASE64" ? source.inheritFormatImage : null,
    elabResultType: source.elabResultType,
    tableUpdateStrategy: {
      typeUpdate: tableUpdateType,
      overrideData: coObject.info.overrideData,
      overrideStyle: coObject.info.overrideStyle,
    },
  };
}

export function mapParameterTableToTgkParameter(parameterTable: DimensionDetails): TgkTableParameter {
  return {
    dimensionOid: parameterTable.dimensionOid,
    code: parameterTable.code,
    structureCode: parameterTable.structureCode,
    values: parameterTable.values,
    inherited: parameterTable.inherited,
    hierarchy: parameterTable.hierarchy
      ? {
          code: parameterTable.hierarchy.code,
          desc: parameterTable.hierarchy.desc,
          oid: parameterTable.hierarchy.oid,
        }
      : null,
  };
}

async function getUpdatedAdvanced(
  rootDocument: DocumentPart,
  advanced: AdvancedDocumentMetadata
): Promise<AdvancedDocumentMetadata> {
  if (canInherit(rootDocument)) {
    if (rootDocument.root) {
      const editDocumentCustomXML = await getDocumentAdvancedCustomXml();
      return {
        ...editDocumentCustomXML.advanced,
        choice: advanced.choice,
      };
    }
    return {
      numberFormat: rootDocument.metadata.formatMetadata.numberFormat.format,
      typeNumber: rootDocument.metadata.formatMetadata.numberType,
      percentageFormat: rootDocument.metadata.formatMetadata.percentageFormat.format,
      typePercentage: rootDocument.metadata.formatMetadata.percentageType,
      dateFormat: rootDocument.metadata.formatMetadata.dateFormat.format,
      typeDate: rootDocument.metadata.formatMetadata.dateType,
      choice: advanced.choice,
      id: advanced.id,
      locale: rootDocument.metadata.formatMetadata.locale,
      inheritFormat: advanced.inheritFormat,
    };
  }
  NotificationManager.addNotification({
    title: "",
    message: formatMessage("cof.createnewobject.inheritformat.error"),
    type: "error",
  });
  return null;
}

export const mapAdvancedTableToDocPartMetadata = (
  documentAdvancedCustomXML: AdvancedDocumentMetadata,
  locale: string
): FormatMetadata => {
  // eslint-disable-next-line no-undef
  console.log("documentAdvancedCustomXML: ", documentAdvancedCustomXML);
  if (documentAdvancedCustomXML.choice === "DOCUMENT_ROOT") {
    return {
      numberFormat: {
        format: documentAdvancedCustomXML.numberFormat,
      },
      numberType: documentAdvancedCustomXML.typeNumber,
      percentageFormat: {
        format: documentAdvancedCustomXML.percentageFormat,
      },
      percentageType: documentAdvancedCustomXML.typePercentage,
      dateFormat: {
        format: documentAdvancedCustomXML.dateFormat,
      },
      dateType: documentAdvancedCustomXML.typeDate,
      locale: documentAdvancedCustomXML.locale,
      choice: documentAdvancedCustomXML.choice,
    };
  } else {
    return {
      numberFormat: {
        format: null,
      },
      numberType: null,
      percentageFormat: {
        format: null,
      },
      percentageType: null,
      dateFormat: {
        format: null,
      },
      dateType: null,
      locale: locale,
      choice: documentAdvancedCustomXML.choice,
    };
  }
};

export function mapTgkAmountConfigToTgkAmount(coObject: CoObjectItem, userId: string): TgkAmountElaborationRequest {
  const source = coObject.source as TgkAmountObjectSource;
  let formatType = coObject.advanced.choice;
  let elabResultType = source.elabResultType;
  let cell = null;
  let area = null;
  let sheet = null;
  let sheetCode = null;
  let formId = null;
  let formDesc = null;
  let dimensions = [];
  let analyticalWorkspace = null;
  let dataset = null;
  let amount = null;
  let parameters = null;
  let objectSource =
    source.objectSource === undefined || source.objectSource === "" ? SourceTypeEnum.TAGETIK : source.objectSource;

  switch (source.objectSource) {
    case SourceTypeEnum.EXCEL:
      formatType = FormatTypeEnum.NUMBER;
      formId = source.form?.code;
      formDesc = source.form?.desc;
      sheetCode = source.sheet.code;
      sheet = source.cell === "" || source.cell == undefined ? getSheetForExcelAmountObject(source) : source.sheet.desc;
      area = source.area?.name;
      cell = source.cell;
      break;
    case SourceTypeEnum.TAGETIK_FORM:
      formatType = FormatTypeEnum.NUMBER;
      formId = source.form?.code;
      formDesc = source.form?.desc;
      sheetCode = source.sheet.code;
      sheet = source.sheet.desc;
      area = source.area?.name;
      cell = source.cell;
      parameters = coObject.parameters?.map((p) => mapParameterTableToTgkParameter(p));
      break;
    default:
      dimensions = Object.values(source.dimensions);
      analyticalWorkspace = source.analyticalWorkspace;
      dataset = source.dataset;
      amount = source.amount;
  }

  return {
    id: coObject.id,
    objectType: CoObjectTypeEnum[coObject.source.type],
    name: coObject.info.objectName,
    desc: coObject.info.info ?? "",
    isComponent: coObject.info.isComponent,
    dimensions: dimensions,
    dbId: source.dbApplication.id,
    documentId: coObject.documentId,
    contentControlId: `${coObject.contentControlId}`,
    clientId: store.getState().configurationState.clientId,
    correlationId: uuid.v4(),
    userId: userId,
    objectId: coObject.info.objectName,
    formatMetadata: mapAdvancedAmountToDocPartMetadata(coObject.advanced),
    isLocked: coObject.isLocked,
    formatType: formatType,
    inheritFormat: coObject.advanced.inheritFormat,
    elabResultType: elabResultType,
    source: objectSource,
    formId: formId,
    formDesc: formDesc,
    sheetCode: sheetCode,
    sheet: sheet,
    area: area,
    analyticalWorkspace: analyticalWorkspace,
    dataset: dataset,
    amount: amount,
    cell: cell,
    ...(parameters && { parameters }),
  };
}

export const mapAdvancedAmountToDocPartMetadataOrUpdate = async (
  rootDocument: DocumentPart,
  advanced: AdvancedDocumentMetadata
): Promise<FormatMetadata> => {
  if (advanced.inheritFormat) {
    advanced = await getUpdatedAdvanced(rootDocument, advanced);
  }
  return mapAdvancedAmountToDocPartMetadata(advanced);
};

export const mapAdvancedTableToDocPartMetadataOrUpdate = async (
  rootDocument: DocumentPart,
  advanced: AdvancedDocumentMetadata
): Promise<FormatMetadata> => {
  if (advanced.choice === "DOCUMENT_ROOT") {
    advanced = await getUpdatedAdvanced(rootDocument, advanced);
  }

  return mapAdvancedTableToDocPartMetadata(advanced, rootDocument.metadata.formatMetadata.locale);
};

export const mapAdvancedAmountToDocPartMetadata = (data: AdvancedDocumentMetadata): FormatMetadata => {
  return {
    numberFormat: {
      format: data.numberFormat,
    },
    numberType: data.typeNumber,
    percentageFormat: {
      format: data.percentageFormat,
    },
    percentageType: data.typePercentage,
    dateFormat: {
      format: data.dateFormat,
    },
    dateType: data.typeDate,
    locale: data.locale,
  };
};

export const mapFakeFormatMetadataForDimensionAttribute = async (
  rootDocument: DocumentPart
): Promise<FormatMetadata> => {
  return {
    numberFormat: {
      format: null,
    },
    percentageFormat: {
      format: null,
    },
    dateFormat: {
      format: null,
    },
    locale: rootDocument.metadata?.formatMetadata.locale,
    choice: null,
  };
};

export function mapTgkDimensionAttributeConfigToTgkDimensionAttribute(
  coObject: CoObjectItem,
  userId: string
): TgkDimensionAttributeElaborationRequest {
  const source = coObject.source as TgkDimensionAttributeSource;
  return {
    id: coObject.id,
    objectType: CoObjectTypeEnum[coObject.info.objectType],
    name: coObject.info.objectName,
    desc: coObject.info.info,
    isComponent: coObject.info.isComponent,
    isLocked: coObject.isLocked,
    dimensions: source.dimensions,
    dbId: source.dbApplication.id,
    documentId: coObject.documentId,
    contentControlId: `${coObject.contentControlId}`,
    clientId: store.getState().configurationState.clientId,
    correlationId: uuid.v4(),
    userId: userId,
    objectId: coObject.info.objectName,
    elabResultType: source.elabResultType,
    formatMetadata: {
      numberFormat: {
        format: null,
      },
      numberType: null,
      percentageFormat: {
        format: null,
      },
      percentageType: null,
      dateFormat: {
        format: null,
      },
      dateType: null,
      locale: source.locale,
      choice: null,
    },
  };
}

export function mapCheckboxConfigToCheckbox(coObject: CoObjectItem): CoObject {
  return {
    isLocked: coObject.isLocked,
    id: coObject.id,
    type: CoObjectTypeEnum[coObject.info.objectType],
    name: coObject.info.objectName,
    desc: coObject.info.info,
    documentId: coObject.documentId,
    contentControlId: `${coObject.contentControlId}`,
  };
}

export function mapDropDownConfigToDropDown(coObject: CoObjectItem): CoObjectDropdown {
  let source: TgkDropDownSource = coObject.source as TgkDropDownSource;
  return {
    isLocked: coObject.isLocked,
    id: coObject.id,
    type: CoObjectTypeEnum[coObject.info.objectType],
    name: coObject.info.objectName,
    desc: coObject.info.info,
    documentId: coObject.documentId,
    contentControlId: `${coObject.contentControlId}`,
    values: source.dropDownList.map((s) => {
      return {
        key: s.key,
        value: s.value,
      };
    }),
  };
}

export function mapFreeTextConfigToFreeText(coObject: CoObjectItem): CoObject {
  return {
    id: coObject.id,
    isLocked: coObject.isLocked,
    documentId: coObject.documentId,
    contentControlId: `${coObject.contentControlId}`,
    name: coObject.info.objectName,
    desc: coObject.info.info,
    type: CoObjectTypeEnum[coObject.info.objectType],
  };
}

export function mapTgkTextConfigToTgkText(coObject: CoObjectItem, userId: string): TgkTextElaborationRequest {
  const source = coObject.source as TgkTextSource;
  return {
    id: coObject.id,
    objectType: CoObjectTypeEnum[coObject.info.objectType],
    name: coObject.info.objectName,
    desc: coObject.info.info ?? "",
    isComponent: coObject.info.isComponent,
    isLocked: coObject.isLocked,
    dimensions: source.dimensions ? Object.values(source.dimensions) : [],
    dbId: source.dbApplication.id,
    documentId: coObject.documentId,
    contentControlId: `${coObject.contentControlId}`,
    clientId: store.getState().configurationState.clientId,
    correlationId: uuid.v4(),
    userId: userId,
    objectId: coObject.info.objectName,
    analyticalWorkspace: source.analyticalWorkspace,
    dataset: source.dataset,
    richText: source.richText,
  };
}

export function mapCheckboxConfigToCheckboxItem(coObject: CoObjectItem): CoObjectItem {
  return {
    isLocked: coObject.isLocked,
    id: coObject.id,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId,
    info: {
      objectName: coObject.info.objectName,
      objectType: CoObjectTypeEnum[coObject.info.objectType],
      info: coObject.info.info,
      isComponent: coObject.info.isComponent,
    },
    source: null,
  };
}

export function mapFreeTextConfigToFreeTextItem(coObject: CoObjectItem): CoObjectItem {
  return {
    isLocked: coObject.isLocked,
    id: coObject.id,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId,
    info: {
      isComponent: coObject.info.isComponent,
      objectName: coObject.info.objectName,
      objectType: coObject.info.objectType,
      info: coObject.info.info,
    },
    source: null,
  };
}

export function mapTgkTextConfigToTgkTextItem(coObject: CoObjectItem): any {
  let source: TgkTextSource = coObject.source as TgkTextSource;
  return {
    id: coObject.id,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId,
    isLocked: coObject.isLocked,
    info: {
      objectName: coObject.info.objectName,
      objectType: coObject.info.objectType,
      info: coObject.info.info,
      isComponent: coObject.info.isComponent,
    },
    source: {
      dimensions: source.dimensions ?? [],
      dbApplication: source.dbApplication,
      analyticalWorkspace: source.analyticalWorkspace,
      dataset: source.dataset,
      richText: source.richText,
      ...(source.process && { process: source.process }),
    },
  };
}

export function mapComponentInstanceToXmlObject(coObject: CoObjectItem): any {
  return {
    id: coObject.id,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId,
    isLocked: coObject.isLocked,
    info: {
      objectName: coObject.info.objectName,
      info: coObject.info.info,
      objectType: coObject.info.objectType,
      componentReference: {
        componentLogicId: coObject.info.componentReference.componentLogicId,
        componentName: coObject.info.componentReference.componentName,
        componentDocumentName: coObject.info.componentReference.componentDocumentName,
      },
    },
  };
}

export function mapTgkTableConfigToTgkTableItem(coObject: CoObjectItem): any {
  let source: TgkTableObjectSource = coObject.source as TgkTableObjectSource;
  return {
    id: coObject.id,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId,
    isLocked: coObject.isLocked,
    info: {
      objectName: coObject.info.objectName,
      objectType: coObject.info.objectType,
      info: coObject.info.info,
      overrideData: coObject.info.overrideData ?? true,
      overrideStyle: coObject.info.overrideStyle ?? true,
      isComponent: coObject.info.isComponent,
    },
    source: {
      objectSource: source.objectSource,
      dbApplication: source.dbApplication,
      form: source.form,
      sheet: source.sheet,
      area: source.area,
      chart: source.chart,
      path: source.path,
      elabResultType: source.elabResultType === "IMAGE_STRINGBASE64" ? "IMAGE_STRINGBASE64" : "STRINGBASE64", //if an old table with HTML elabResultType then override
      ...(source.elabResultType === "IMAGE_STRINGBASE64" && !source.inheritFormatImage
        ? {
            imageExtension: source.imageExtension,
            ...(source.imageResolution ? { imageResolution: source.imageResolution } : {}),
          }
        : {}),
      inheritFormatImage: source.elabResultType === "IMAGE_STRINGBASE64" ? source.inheritFormatImage : null,
    },
    advanced: coObject.advanced,
    parameters: coObject.parameters?.map((p) => mapParameterTableToTgkParameter(p)),
    inheritFormat: coObject.advanced.inheritFormat,
  };
}

export function mapTgkAmountConfigToTgkAmountItem(coObject: CoObjectItem): any {
  let source: TgkAmountObjectSource = coObject.source as TgkAmountObjectSource;
  let formatType = coObject.advanced.choice;
  if (source.objectSource === SourceTypeEnum.EXCEL) {
    formatType = FormatTypeEnum.NUMBER;
  }
  return {
    id: coObject.id,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId,
    isLocked: coObject.isLocked,
    info: {
      objectName: coObject.info.objectName,
      objectType: coObject.info.objectType,
      info: coObject.info.info,
      isComponent: coObject.info.isComponent,
    },
    source: {
      dimensions: source.dimensions,
      dbApplication: source.dbApplication,
      objectSource: source.objectSource,
      form: source.form,
      sheet: source.sheet,
      area: source.area,
      cell: source.cell,
      analyticalWorkspace: source.analyticalWorkspace,
      dataset: source.dataset,
      amount: source.amount,
      type: source.type,
      ...(source.process && { process: source.process }),
    },
    ...(coObject.parameters && { parameters: coObject.parameters?.map((p) => mapParameterTableToTgkParameter(p)) }),
    advanced: coObject.advanced,
    inheritFormat: coObject.advanced.inheritFormat,
    formatType: formatType,
  };
}

export function mapTgkDimensionAttributeConfigToTgkDimensionAttributeItem(coObject: CoObjectItem): any {
  let source: TgkDimensionAttributeSource = coObject.source as TgkDimensionAttributeSource;
  return {
    id: coObject.id,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId,
    isLocked: coObject.isLocked,
    info: {
      objectName: coObject.info.objectName,
      objectType: coObject.info.objectType,
      info: coObject.info.info,
      isComponent: coObject.info.isComponent,
    },
    source: {
      dimensions: source.dimensions,
      dbApplication: source.dbApplication,
    },
  };
}

export function mapDropDownConfigToDropDownItem(coObject: CoObjectItem): any {
  let source: TgkDropDownSource = coObject.source as TgkDropDownSource;
  return {
    id: coObject.id,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId,
    isLocked: coObject.isLocked,
    info: {
      objectName: coObject.info.objectName,
      objectType: coObject.info.objectType,
      info: coObject.info.info,
      isComponent: coObject.info.isComponent,
    },
    source: {
      dropDownList: source.dropDownList,
    },
  };
}

export function mapDocumentPartConfigToDocumentPart(
  coObject: CoObjectItem,
  position?: DocumentPartRelativePosition,
  multilinked?: boolean
): CoObjectDocumentPartInclude {
  const source: DocumentPartSource = coObject.source as DocumentPartSource;
  return {
    order: getNewOrderFromRelativePosition(position),
    documentPartMetadata: {
      documentId: null,
      id: null,
      formatMetadata: multilinked ? mapAdvancedDocumentMetadataToFormatMetadata(coObject.advanced) : null,
      parametersMetadata: coObject.parameters as DocumentParameters[],
      ...(multilinked && { language: coObject.advanced.locale }),
    },
    id: coObject.id,
    documentId: coObject.documentId,
    contentControlId: `${coObject.contentControlId}`,
    name: normalizeDocumentName(coObject.info.objectName),
    desc: coObject.info.info,
    type: CoObjectTypeEnum[coObject.info.objectType],
    path: normalizePath(source.path),
    includedDocumentPartId: source.includedDocumentPartId,
  };
}

export function mapAdvancedDocumentMetadataToFormatMetadata(advanced: AdvancedDocumentMetadata): any {
  return {
    numberFormat: { format: advanced.numberFormat, decimals: 0, changeSign: false },
    numberType: advanced.typeNumber,
    percentageFormat: { format: advanced.percentageFormat, decimals: 0, changeSign: false },
    percentageType: advanced.typePercentage,
    dateFormat: { format: advanced.dateFormat },
    dateType: advanced.typeDate,
    choice: advanced.choice,
  };
}

export function mapDocumentPartConfigToDocumentPartItem(coObject: CoObjectItem): any {
  let source: DocumentPartSource = coObject.source as DocumentPartSource;
  return {
    id: coObject.id,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId,
    info: {
      objectName: coObject.info.objectName,
      objectType: CoObjectTypeEnum[coObject.info.objectType],
      info: coObject.info.info,
    },
    source: {
      path: source.path,
      includedDocumentPartId: source.includedDocumentPartId,
    },
  };
}

export function mapDocumentPartIncludeToDocumentPartItem(coObject: CoObjectDocumentPartInclude): any {
  return {
    id: coObject.id,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId,
    info: {
      objectName: coObject.name,
      objectType: coObject.type,
    },
    source: {
      path: coObject.path,
      includedDocumentPartId: coObject.includedDocumentPartId,
    },
  };
}

const getNewOrderFromRelativePosition = (position?: DocumentPartRelativePosition) => {
  if (!position) return -1;
  if (position.relativePosition === "after") return position.contentControlOrder + 1;
  return position.contentControlOrder;
};

export const getUpdatedDocumentParameters = (
  documentParameters: DocumentParameters[],
  fatherDocumentParameters: DocumentParameters[]
) => {
  const newDocumentParameters: DocumentParameters[] = documentParameters?.map((documentParameter) => {
    const thisDbfatherDocumentParameter = fatherDocumentParameters?.find((dp) => dp.dbId === documentParameter.dbId);
    return {
      dbId: documentParameter.dbId,
      dimensions: getUpdatedParameters(documentParameter.dimensions, thisDbfatherDocumentParameter?.dimensions ?? []),
    };
  });
  return newDocumentParameters;
};

export const getUpdatedParameters = (parameters: CoObjectParameters, inheritableDbParameters: DimensionDetails[]) => {
  const newParameters: CoObjectParameters = [];

  parameters.forEach((item) => {
    if (!item.inherited) {
      newParameters.push(item);
      return;
    }
    const thisDimensionsInheritableParameters = inheritableDbParameters
      ?.filter((d) => d.structureCode === item.structureCode)
      .map((p) => ({ ...p, code: item.code, inherited: true }));
    if (!thisDimensionsInheritableParameters?.length) {
      NotificationManager.addNotification({
        title: "",
        message: formatMessage("cof.createnewobject.inheritparameters.error"),
        type: "error",
      });
    }
    newParameters.push(...thisDimensionsInheritableParameters);
  });

  return newParameters;
};

export const getUpdatedDimensions = (dimensions: DimensionDetails[], inheritableDbParameters: DimensionDetails[]) => {
  const inheritedDimensions = new Map<string, DimensionDetails>();
  let attributeDetail: { dimensionOid: string; attributeCode: string } = null;
  const newDimensions: DimensionDetails[] = [];

  dimensions.forEach((item) => {
    if (item.attributeCode)
      attributeDetail = {
        dimensionOid: item.dimensionOid,
        attributeCode: item.attributeCode,
      };
    if (item.inherited) {
      inheritedDimensions.set(item.dimensionOid, item);
    } else {
      newDimensions.push(item);
    }
  });

  inheritedDimensions.forEach((dimension, oid) => {
    const dimensionsInheritableParameters = getEnrichedInheritableParameters(
      oid,
      dimension,
      attributeDetail,
      inheritableDbParameters
    );

    if (!dimensionsInheritableParameters?.length) {
      NotificationManager.addNotification({
        title: "",
        message: formatMessage("cof.createnewobject.inheritparameters.error"),
        type: "error",
      });
    }
    newDimensions.push(...dimensionsInheritableParameters);
  });

  return newDimensions;
};

const getEnrichedInheritableParameters = (
  oid: string,
  dimension: DimensionDetails,
  attributeDetail: { dimensionOid: string; attributeCode: string } | null,
  inheritableDbParameters: DimensionDetails[]
): DimensionDetails[] => {
  const isDimensionAttributeFirstDimension = attributeDetail && oid === attributeDetail.dimensionOid;

  return (
    inheritableDbParameters
      ?.filter((d) => d.dimensionOid === oid)
      .map((p) => ({
        ...p,
        inherited: true,
        label: dimension.label,
        type: dimension.type,
        ...(isDimensionAttributeFirstDimension && { attributeCode: attributeDetail.attributeCode }),
      })) || []
  );
};

function getSheetForExcelTableObject(source: TgkTableObjectSource) {
  return checkArea(source);
}

function getSheetForExcelAmountObject(source: TgkAmountObjectSource) {
  return checkArea(source);
}

function checkArea(source: any) {
  if (source.area?.name?.includes("!")) {
    let newSheet = source.area?.name?.split("!")[0];
    if (source.sheet?.desc !== newSheet) {
      return newSheet;
    }
  }
  return source.sheet?.desc;
}

export async function mapCoObjectToRefreshElaborationItem(
  correlationId: string,
  rootDocument: DocumentPart,
  coObject: CoObjectItem,
  selectedUpdateOption?: UpdateOptions,
  tableUpdateType?: TableUpdateStrategy["typeUpdate"],
  fileExcelName?: string
) {
  if (coObject.info.objectType === "TABLE" || coObject.info.objectType === "CHART") {
    const source = coObject.source as TgkTableObjectSource;
    const newFormatMetadata = await mapAdvancedTableToDocPartMetadataOrUpdate(rootDocument, coObject.advanced);

    const item: ElaborationItem = {
      correlationId: correlationId,
      contentControlId: coObject.contentControlId.toString(),
      componentLogicId: coObject.info?.componentReference?.componentLogicId,
      dbId: source.dbApplication?.id,
      formId: source.form.code,
      formDesc: source.objectSource === SourceTypeEnum.EXCEL && fileExcelName ? fileExcelName : source.form.desc,
      sheetCode: source.sheet.code,
      sheet:
        source.objectSource !== SourceTypeEnum.EXCEL || coObject.info?.objectType === "CHART"
          ? source.sheet.desc
          : getSheetForExcelTableObject(source),
      area: source.area?.name,
      chart: source.chart?.name,
      objectId: coObject.info.objectName,
      objectType: coObject.info.objectType,
      documentPartIncludedId: null,
      type: "REFRESH",
      formatMetadata: newFormatMetadata,
      ...(!source.inheritFormatImage
        ? {
            formatImage: {
              imageExtension: source.imageExtension,
              ...(source.imageResolution ? { imageResolution: source.imageResolution } : {}),
            },
          }
        : {}),
      parameters: coObject.parameters?.map((p) => mapParameterTableToTgkParameter(p)),
      inheritFormat: coObject.advanced.inheritFormat,
      inheritFormatImage: source.inheritFormatImage,
      elabResultType: source.elabResultType,
      source: source.objectSource,
      tableUpdateStrategy: {
        typeUpdate: tableUpdateType,
        overrideData: coObject.info.overrideData,
        overrideStyle: coObject.info.overrideStyle,
      },
    };
    return item;
  }
  if (coObject.info.objectType === "AMOUNT") {
    const source = coObject.source as TgkAmountObjectSource;
    const newFormatMetadata = await mapAdvancedAmountToDocPartMetadataOrUpdate(rootDocument, coObject.advanced);
    let formatType = coObject.advanced.choice;
    let elabResultType = coObject.source?.elabResultType;
    let cell = null;
    let area = null;
    let sheet = null;
    let sheetCode = null;
    let formId = null;
    let formDesc = null;
    let parameters = null;
    let objectSource =
      source.objectSource === undefined || source.objectSource === "" ? SourceTypeEnum.TAGETIK : source.objectSource;

    switch (source.objectSource) {
      case SourceTypeEnum.EXCEL:
        formatType = FormatTypeEnum.NUMBER;
        formId = source.form?.code;
        formDesc = fileExcelName ? fileExcelName : source.form.desc;
        sheetCode = source.sheet.code;
        sheet = source.sheet.desc;
        area = source.area.name;
        cell = source.cell;
        break;
      case SourceTypeEnum.TAGETIK_FORM:
        formatType = FormatTypeEnum.NUMBER;
        formId = source.form?.code;
        formDesc = source.form?.desc;
        sheetCode = source.sheet.code;
        sheet = source.sheet.desc;
        area = source.area.name;
        cell = source.cell;
        parameters = coObject.parameters?.map((p) => mapParameterTableToTgkParameter(p));
        break;
    }

    const item: ElaborationItem = {
      correlationId: correlationId,
      contentControlId: coObject.contentControlId.toString(),
      dbId: source.dbApplication.id,
      dimensions: source.dimensions,
      formId: formId,
      formDesc: formDesc,
      area: area,
      sheetCode: sheetCode,
      sheet: sheet,
      cell: cell,
      objectId: coObject.info.objectName,
      objectType: source.analyticalWorkspace?.code ? "AMOUNT_AIH" : "AMOUNT",
      documentPartIncludedId: null,
      type: "REFRESH",
      formatMetadata: newFormatMetadata,
      inheritFormat: coObject.advanced.inheritFormat,
      formatType: formatType,
      elabResultType: elabResultType,
      source: objectSource,
      analyticalWorkspace: source.analyticalWorkspace?.code ? source.analyticalWorkspace : null,
      dataset: source.dataset?.code ? source.dataset : null,
      process: source.process,
      amount: source.amount,
      ...(parameters && { parameters }),
    };
    return item;
  }
  if (coObject.info.objectType === "DIMENSION_ATTRIBUTE") {
    const source = coObject.source as TgkDimensionAttributeSource;
    const newFormatMetadata = await mapFakeFormatMetadataForDimensionAttribute(rootDocument);
    const item: ElaborationItem = {
      correlationId: correlationId,
      contentControlId: coObject.contentControlId.toString(),
      dbId: source.dbApplication.id,
      dimensions: source.dimensions,
      formId: null,
      area: null,
      sheet: null,
      objectId: coObject.info.objectName,
      objectType: coObject.info.objectType,
      documentPartIncludedId: null,
      type: "REFRESH",
      formatMetadata: newFormatMetadata,
      elabResultType: coObject.source?.elabResultType,
    };
    return item;
  }
  if (coObject.info.objectType === "TGK_TEXT") {
    const source = coObject.source as TgkTextSource;
    const item: ElaborationItem = {
      correlationId: correlationId,
      contentControlId: coObject.contentControlId.toString(),
      dbId: source.dbApplication.id,
      dimensions: source.dimensions ?? [],
      analyticalWorkspace: source.analyticalWorkspace,
      dataset: source.dataset,
      richText: source.richText,
      process: source.process,
      objectId: coObject.info.objectName,
      objectType: coObject.info.objectType,
      documentPartIncludedId: null,
      type: "REFRESH",
      elabResultType: coObject.source?.elabResultType,
    };
    return item;
  }
  if (coObject.info.objectType === "DOCUMENT_PART_INCLUDE") {
    const source = coObject.source as DocumentPartSource;
    const item: ElaborationItem = {
      correlationId: correlationId,
      contentControlId: coObject.contentControlId.toString(),
      dbId: null,
      formId: null,
      area: null,
      sheet: null,
      objectId: coObject.info.objectName,
      objectType: coObject.info.objectType,
      documentPartIncludedId: source.includedDocumentPartId,
      type: "REFRESH",
      updateOptions: selectedUpdateOption,
      elabResultType: coObject.source?.elabResultType,
      tableUpdateStrategy: {
        typeUpdate: tableUpdateType,
        overrideData: coObject.info.overrideData,
        overrideStyle: coObject.info.overrideStyle,
      },
    };
    return item;
  }
  throw new Error(`Unsupported object type ${coObject.info.objectType}`);
}
